import { registerFigure, registerOverlay } from 'klinecharts'
import type { OverlayCreateFiguresCallbackParams } from 'klinecharts'
import { CHART_FONT_FAMILY } from './chartColors'
import { type OverlayFigure } from './overlayUtils'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

export const BAR_COUNTDOWN_PILL_NAME = 'barCountdownPill'
export const BAR_COUNTDOWN_PILL_FIGURE = 'barCountdownPillFigure'
/** Overlay zLevel only sorts (KC:5876-5885): this keeps the pill above other
 *  axis pills, and the crosshair label still paints after every overlay. */
export const BAR_COUNTDOWN_Z_LEVEL = 1000

/** Metrics of the DOM tag this replaces (EdgeChart.tsx:3114-3139). */
export const PILL = {
  padX: 5,
  padY: 2,
  priceSize: 11,
  countdownSize: 9,
  lineHeight: 1.2,
  radius: 2,
  yOffset: 9, // centres the price row on the line
  text: '#ffffff',
  countdownAlpha: 0.92,
} as const

/** Whole seconds → compact countdown: "M:SS", or "H:MM:SS" once past an hour. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

export interface BarCountdownPillState {
  close: number
  price: string
  countdown: string
  color: string
  /** Scales hidden: the pill draws in the main pane, not on the axis. */
  collapsed: boolean
}

export function barCountdownPillState(
  anchor: { close: number; price: string; color: string; barEnd: number } | null,
  now: number,
  collapsed: boolean,
): BarCountdownPillState | null {
  if (!anchor) return null
  // Clamp to 0 rather than hiding once the bar's nominal end has passed: a live
  // feed can lag past the close before the next bar arrives.
  const seconds = Math.max(0, Math.ceil((anchor.barEnd - now) / 1000))
  return {
    close: anchor.close,
    price: anchor.price,
    color: anchor.color,
    countdown: formatCountdown(seconds),
    collapsed,
  }
}

/** The native label's pinning: convertToNicePixel (KC:10903-10908). */
export function nicePillY(rawY: number, paneHeight: number): number {
  return Math.round(Math.max(paneHeight * 0.05, Math.min(rawY, paneHeight * 0.98)))
}

export function layoutBarCountdownPill(input: {
  x: number
  y: number
  fromZero: boolean
  priceWidth: number
  countdownWidth: number
}) {
  const priceLine = PILL.priceSize * PILL.lineHeight
  const countdownLine = PILL.countdownSize * PILL.lineHeight
  const width = Math.ceil(Math.max(input.priceWidth, input.countdownWidth)) + PILL.padX * 2
  const height = PILL.padY * 2 + priceLine + countdownLine
  const top = Math.round(input.y) - PILL.yOffset
  const left = input.fromZero ? input.x : input.x - width
  return {
    left,
    top,
    width,
    height,
    centerX: left + width / 2,
    priceY: top + PILL.padY + priceLine / 2,
    countdownY: top + PILL.padY + priceLine + countdownLine / 2,
  }
}

export function buildBarCountdownPillFigures(input: {
  state: BarCountdownPillState | null
  layer: 'axis' | 'pane'
  boundingWidth: number
  boundingHeight: number
  fromZero: boolean
  rawY: number
}): OverlayFigure[] {
  const { state } = input
  if (!state || !Number.isFinite(input.rawY)) return []
  // Axis shown: the y-axis canvas. Scales hidden: the strip can be 0 px wide
  // (KC:10782-10849), so the main pane draws it instead.
  if (state.collapsed !== (input.layer === 'pane')) return []
  return [
    {
      type: BAR_COUNTDOWN_PILL_FIGURE,
      ignoreEvent: true,
      attrs: {
        x: input.fromZero ? 0 : input.boundingWidth,
        y: nicePillY(input.rawY, input.boundingHeight),
        fromZero: input.fromZero,
        price: state.price,
        countdown: state.countdown,
      },
      styles: { backgroundColor: state.color, color: PILL.text, family: CHART_FONT_FAMILY },
    },
  ]
}

interface PillAttrs {
  x: number
  y: number
  fromZero: boolean
  price: string
  countdown: string
}

interface PillStyles {
  backgroundColor?: string
  color?: string
  family?: string
}

/** Two rows share one box, which the stock `text` figure cannot do (it draws a
 *  background per text). Precedent: iconChipFigure.ts. */
const registerBarCountdownPillFigure = registerOnce(() => {
  registerFigure({
    name: BAR_COUNTDOWN_PILL_FIGURE,
    checkEventOn: () => false,
    draw: (ctx: CanvasRenderingContext2D, attrs: unknown, styles: unknown) => {
      const a = attrs as PillAttrs
      const s = (styles ?? {}) as PillStyles
      const family = s.family ?? CHART_FONT_FAMILY
      ctx.font = `normal ${PILL.priceSize}px ${family}`
      const priceWidth = ctx.measureText(a.price).width
      ctx.font = `normal ${PILL.countdownSize}px ${family}`
      // Canvas has no tabular-nums: sizing from zeros keeps the box from
      // breathing every second.
      const countdownWidth = ctx.measureText(a.countdown.replace(/\d/g, '0')).width
      const l = layoutBarCountdownPill({
        x: a.x,
        y: a.y,
        fromZero: a.fromZero,
        priceWidth,
        countdownWidth,
      })

      ctx.beginPath()
      const roundRect = (
        ctx as CanvasRenderingContext2D & {
          roundRect?: (x: number, y: number, w: number, h: number, r: number) => void
        }
      ).roundRect
      if (roundRect) roundRect.call(ctx, l.left, l.top, l.width, l.height, PILL.radius)
      else ctx.rect(l.left, l.top, l.width, l.height)
      ctx.closePath()
      ctx.fillStyle = s.backgroundColor ?? '#4b5563'
      ctx.fill()

      ctx.fillStyle = s.color ?? PILL.text
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `normal ${PILL.priceSize}px ${family}`
      ctx.fillText(a.price, l.centerX, l.priceY)
      ctx.save()
      ctx.globalAlpha = PILL.countdownAlpha
      ctx.font = `normal ${PILL.countdownSize}px ${family}`
      ctx.fillText(a.countdown, l.centerX, l.countdownY)
      ctx.restore()
    },
  } as Parameters<typeof registerFigure>[0])
})

const figures =
  (layer: 'axis' | 'pane') =>
  ({ overlay, bounding, yAxis }: OverlayCreateFiguresCallbackParams) => {
    const ext = (overlay.extendData ?? {}) as {
      read?: (now: number) => BarCountdownPillState | null
    }
    const state = ext.read?.(Date.now()) ?? null
    if (!state || !yAxis) return []
    return buildBarCountdownPillFigures({
      state,
      layer,
      boundingWidth: bounding.width,
      boundingHeight: bounding.height,
      fromZero: yAxis.isFromZero(),
      rawY: yAxis.convertToPixel(state.close),
    })
  }

/** The state is read at paint time through `extendData.read`, so the overlay's
 *  point is never used for position and no per-tick data is pushed into klinecharts. */
export const registerBarCountdownPillOverlay = registerOnce(() => {
  registerBarCountdownPillFigure()
  registerOverlay({
    name: BAR_COUNTDOWN_PILL_NAME,
    totalStep: 2,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: figures('pane'),
    createYAxisFigures: figures('axis'),
  } as OverlayTemplateParam)
})
